import React, { useEffect, useState } from 'react';
import { Routes, Route } from 'react-router-dom';
import styled from 'styled-components';
import { useTelegram } from './hooks/useTelegram';
import { CartProvider } from './context/CartContext';
import { FavoritesProvider } from './context/FavoritesContext';
import Layout from './components/Layout/Layout';
import Home from './pages/Home/Home';
import Catalog from './pages/Catalog/Catalog';
import Search from './pages/Search/Search';
import Favorites from './pages/Favorites/Favorites';
import Cart from './pages/Cart/Cart';
import Profile from './pages/Profile/Profile';
import GameDetails from './pages/GameDetails/GameDetails';
import Category from './pages/Category/Category';
import PSPlus from './pages/PSPlus/PSPlus';
import PSPlusDetail from './pages/PSPlusDetail/PSPlusDetail';
import Donates from './pages/Donates/Donates';
import Preorders from './pages/Preorders/Preorders';
import Services from './pages/Services/Services';
import Xbox from './pages/Xbox/Xbox';
import GamePass from './pages/Xbox/GamePass';
import LoadingScreen from './components/LoadingScreen/LoadingScreen';

const AppWrap = styled.div`
  min-height: 100vh;
  background: #0a0a0f;
`;

class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  render() {
    if (this.state.error) return (
      <div style={{padding:20,color:'#ff3b30',fontSize:13,wordBreak:'break-all'}}>
        <h3>Ошибка рендера:</h3>
        <pre style={{whiteSpace:'pre-wrap'}}>{this.state.error.message}{'\n'}{this.state.error.stack}</pre>
      </div>
    );
    return this.props.children;
  }
}

/* Small expand button — bottom left, like @relocturkey_bot */
function ExpandButton({ onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        position: 'fixed',
        bottom: 80,
        left: 12,
        zIndex: 99999,
        width: 36,
        height: 36,
        borderRadius: '50%',
        background: 'rgba(0,111,255,0.85)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        boxShadow: '0 2px 12px rgba(0,0,0,0.5)',
        WebkitTapHighlightColor: 'transparent',
        backdropFilter: 'blur(8px)',
      }}
    >
      {/* Fullscreen expand icon (arrows pointing outward) */}
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="15 3 21 3 21 9" />
        <polyline points="9 21 3 21 3 15" />
        <line x1="21" y1="3" x2="14" y2="10" />
        <line x1="3" y1="21" x2="10" y2="14" />
      </svg>
    </div>
  );
}

function App() {
  const { tg } = useTelegram();
  const [loading, setLoading] = useState(true);
  const [showExpand, setShowExpand] = useState(false);

  useEffect(() => {
    if (tg) {
      tg.ready();
      tg.expand();
      // Disable swipe-to-close
      if (typeof tg.disableVerticalSwipes === 'function') {
        tg.disableVerticalSwipes();
      } else if (tg.isVerticalSwipesEnabled !== undefined) {
        tg.isVerticalSwipesEnabled = false;
      }
      tg.isClosingConfirmationEnabled = true;
      tg.setHeaderColor('#0a0a0f');
      tg.setBackgroundColor('#0a0a0f');
      tg.BackButton.hide();

      // Keep expanding
      tg.expand();
      setTimeout(() => { try { tg.expand(); } catch(e){} }, 500);

      // Fullscreen events (for expand button)
      if (tg.onEvent) {
        try {
          tg.onEvent('fullscreenChanged', () => {
            if (tg.isFullscreen) setShowExpand(false);
          });
          tg.onEvent('fullscreenFailed', () => {
            setShowExpand(true);
          });
        } catch(e) {}
      }

      // Show expand button if requestFullscreen is available but not in fullscreen
      setTimeout(() => {
        if (typeof tg.requestFullscreen === 'function' && !tg.isFullscreen) {
          setShowExpand(true);
        }
      }, 1000);
    }

    // Prevent pull-to-close when scrolled to top
    const root = document.getElementById('root');
    let touchStartY = 0;
    const onTouchStart = (e) => { touchStartY = e.touches[0].clientY; };
    const onTouchMove = (e) => {
      if (!root) return;
      const dy = e.touches[0].clientY - touchStartY;
      if (root.scrollTop <= 0 && dy > 0) {
        e.preventDefault();
      }
    };
    document.addEventListener('touchstart', onTouchStart, { passive: true });
    document.addEventListener('touchmove', onTouchMove, { passive: false });

    setTimeout(() => setLoading(false), 800);

    return () => {
      document.removeEventListener('touchstart', onTouchStart);
      document.removeEventListener('touchmove', onTouchMove);
    };
  }, [tg]);

  const handleExpand = () => {
    if (tg) {
      tg.expand();
      try {
        if (typeof tg.requestFullscreen === 'function') {
          tg.requestFullscreen();
        }
      } catch(e){}
      setShowExpand(false);
    }
  };

  if (loading) return <LoadingScreen />;

  return (
    <CartProvider>
      <FavoritesProvider>
        <AppWrap>
          <ErrorBoundary>
          <Layout>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/catalog" element={<Catalog />} />
              <Route path="/search" element={<Search />} />
              <Route path="/favorites" element={<Favorites />} />
              <Route path="/cart" element={<Cart />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/game/:id" element={<GameDetails />} />
              <Route path="/category/:categoryId" element={<Category />} />
              <Route path="/psplus" element={<PSPlus />} />
              <Route path="/psplus/:tier/:duration" element={<PSPlusDetail />} />
              <Route path="/donates" element={<Donates />} />
              <Route path="/donates/:subtype" element={<Donates />} />
              <Route path="/preorders" element={<Preorders />} />
              <Route path="/services" element={<Services />} />
              <Route path="/xbox" element={<Xbox />} />
              <Route path="/xbox/gamepass" element={<GamePass />} />
              <Route path="*" element={<Home />} />
            </Routes>
          </Layout>
          {showExpand && <ExpandButton onClick={handleExpand} />}
          </ErrorBoundary>
        </AppWrap>
      </FavoritesProvider>
    </CartProvider>
  );
}

export default App;
